import type { ComponentType, ReactNode, SVGProps } from 'react';
import { FILE_KIND_LABEL, type FileKind } from '@bms2/shared';
import styles from './appGlyphs.module.css';

/**
 * 🔒 The glyphs the application draws itself, where no Lucide icon carries the
 * meaning (decisions 3011 and 3012). They are drawn as inline SVG through the
 * one `Icon` control, never an emoji and never a raster, and each is named in
 * the icon reference under an `app-` name so no Lucide name is ever shadowed.
 *
 * The file-type icon is the owner's own set of 2026-10-11 (*"here are some
 * icons to use"*): a white page with its top-right corner folded and a thick
 * outline in the text colour, a glyph in its middle, and a black rounded band
 * across its foot, a little wider than the page, reading the kind in white
 * bold capitals.
 */
type Glyph = ComponentType<SVGProps<SVGSVGElement>>;

/** The glyph in the page's middle, one per kind of work, drawn in the outline's ink. */
const middles: Readonly<Record<FileKind, ReactNode>> = {
  pdf: <path d="M17 23c3-1 5.5-6 7-12 1.5 6 4 11 7 12-4-1.2-10-1.2-14 0z" />,
  doc: <path d="M17 11h14M17 15h14M17 19h14M17 23h9" />,
  xls: <path d="M16 10h16v14H16zM16 14.7h16M16 19.3h16M21.3 10v14M26.7 10v14" />,
  csv: <path d="M17 12h3.5M23 12h3M29 12h2M17 17h3.5M23 17h3M29 17h2M17 22h3.5M23 22h3M29 22h2" />,
  txt: <path d="M17.5 11h13M24 11v13" />,
  jpg: <><path d="M16 10h16v14H16zM16 22l5-6 4 4 3-3 4 5" /><circle cx="28" cy="13.6" r="1.4" className={styles.ink} /></>,
  png: <><path d="M16 10h16v14H16zM16 22l5-6 4 4 3-3 4 5" /><circle cx="28" cy="13.6" r="1.4" className={styles.ink} /></>,
  gif: <><path d="M16 10h16v14H16zM16 22l5-6 4 4 3-3 4 5" /><circle cx="28" cy="13.6" r="1.4" className={styles.ink} /></>,
  svg: <path d="M17 22c2-10 12-10 14 0M17 22l-1.6-1.6M31 22l1.6-1.6M15.5 21h3v3h-3zM29.5 21h3v3h-3zM22.6 11.2h2.8v2.8h-2.8z" />,
  zip: <path d="M24 8v12M22 10h2M24 12h2M22 14h2M24 16h2M22 18h2M22 20h4v5h-4z" />,
  dwg: <path d="M17 24V11l13 13zM20 21v-3l3 3z" />,
  dxf: <path d="M17 24V11l13 13zM20 21v-3l3 3z" />,
  dgn: <path d="M17 24V11l13 13zM20 21v-3l3 3z" />,
  ppt: <path d="M16 10h16v10H16zM24 20v4M20 24h8M20 17v-2M24 17v-4M28 17v-3" />,
  json: <path d="M21 10c-2 0-2 1-2 3v2c0 1-1 2-2 2 1 0 2 1 2 2v2c0 2 0 3 2 3M27 10c2 0 2 1 2 3v2c0 1 1 2 2 2-1 0-2 1-2 2v2c0 2 0 3-2 3" />,
  xml: <path d="M20 13l-3.5 4 3.5 4M28 13l3.5 4-3.5 4M25.5 11l-3 12" />,
  html: <><circle cx="24" cy="17" r="6.5" /><path d="M17.5 17h13M24 10.5c-2.6 3.8-2.6 9.2 0 13M24 10.5c2.6 3.8 2.6 9.2 0 13" /></>,
  mp3: <><path d="M22 22V11l8-2v11" /><circle cx="20" cy="22" r="2" className={styles.ink} /><circle cx="28" cy="20" r="2" className={styles.ink} /></>,
  mp4: <><rect x="16" y="10" width="16" height="14" rx="2" /><path d="M22 13.5v7l6-3.5z" className={styles.ink} /></>,
  etc: <><circle cx="19" cy="17" r="1.6" className={styles.ink} /><circle cx="24" cy="17" r="1.6" className={styles.ink} /><circle cx="29" cy="17" r="1.6" className={styles.ink} /></>,
};

function fileKindGlyph(kind: FileKind): Glyph {
  const label = FILE_KIND_LABEL[kind];
  function FileKindGlyph(props: SVGProps<SVGSVGElement>) {
    return (
      <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" data-file-kind={kind} {...props}>
        <path className={styles.page} d="M11 3h18l8 8v34H11z" />
        <path className={styles.fold} d="M29 3v8h8" />
        <g className={styles.middle}>{middles[kind]}</g>
        <rect className={styles.band} x="7" y="28" width="34" height="14" rx="3" />
        <text className={styles.bandText} x="24" y="39.3" textAnchor="middle" data-file-kind-label={label} {...(label.length > 3 ? { textLength: 28, lengthAdjust: 'spacingAndGlyphs' } : {})}>
          {label}
        </text>
      </svg>
    );
  }
  return FileKindGlyph;
}

/** 🔒 The scanner's mark beneath a clean file's icon (decision 3012): a green shield with a white check. */
function ScanCleanGlyph(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path className={styles.shield} d="M12 2l8 3v6c0 5.5-3.5 9.5-8 11-4.5-1.5-8-5.5-8-11V5z" />
      <path className={styles.shieldCheck} d="M8 12l3 3 5-6" />
    </svg>
  );
}

/** Each application glyph under the name the icon reference gives it. */
export const appGlyphs = {
  'app-file-pdf': fileKindGlyph('pdf'),
  'app-file-doc': fileKindGlyph('doc'),
  'app-file-xls': fileKindGlyph('xls'),
  'app-file-csv': fileKindGlyph('csv'),
  'app-file-txt': fileKindGlyph('txt'),
  'app-file-jpg': fileKindGlyph('jpg'),
  'app-file-png': fileKindGlyph('png'),
  'app-file-gif': fileKindGlyph('gif'),
  'app-file-svg': fileKindGlyph('svg'),
  'app-file-zip': fileKindGlyph('zip'),
  'app-file-dwg': fileKindGlyph('dwg'),
  'app-file-dxf': fileKindGlyph('dxf'),
  'app-file-dgn': fileKindGlyph('dgn'),
  'app-file-ppt': fileKindGlyph('ppt'),
  'app-file-json': fileKindGlyph('json'),
  'app-file-xml': fileKindGlyph('xml'),
  'app-file-html': fileKindGlyph('html'),
  'app-file-mp3': fileKindGlyph('mp3'),
  'app-file-mp4': fileKindGlyph('mp4'),
  'app-file-etc': fileKindGlyph('etc'),
  'app-scan-clean': ScanCleanGlyph,
} as const satisfies Record<string, Glyph>;
